import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Minus, Trash2, Package, Search, X } from "lucide-react";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const MAX_QTY = 99;

export const productSubtotal = (rows) => rows.reduce((t, r) => t + (r.price || 0) * r.qty, 0);

// New-booking product lines (local only). Prices shown are the catalog price; the server
// re-snapshots price/name on save and never touches stock until POS billing.
export function BookingProductsPicker({ rows, setRows }) {
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");
  const { data: products } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });
  const available = (products || []).filter((p) => p.active !== false && !rows.some((r) => r.product_id === p.id)
    && p.name.toLowerCase().includes(q.trim().toLowerCase()));
  const add = (p) => { setRows([...rows, { product_id: p.id, name: p.name, price: p.selling_price || 0, qty: 1 }]); setAdding(false); setQ(""); };
  const setQty = (pid, n) => setRows(rows.map((r) => (r.product_id === pid ? { ...r, qty: Math.min(MAX_QTY, Math.max(1, n)) } : r)));
  return (
    <div className="space-y-2" data-testid="bk-products">
      <p className="text-xs font-medium text-slate-600 flex items-center gap-1"><Package className="h-3.5 w-3.5" /> Products</p>
      {rows.map((r) => (
        <div key={r.product_id} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2" data-testid={`bk-product-${r.product_id}`}>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{r.name} <span className="text-xs text-slate-400">· {inr(r.price)}</span></span>
          <div className="flex items-center rounded-lg border border-slate-200">
            <button type="button" onClick={() => setQty(r.product_id, r.qty - 1)} disabled={r.qty <= 1} aria-label="Decrease quantity" data-testid={`bk-product-dec-${r.product_id}`} className="h-8 w-8 flex items-center justify-center disabled:text-slate-300"><Minus className="h-3.5 w-3.5" /></button>
            <span className="min-w-[1.5rem] text-center text-sm font-semibold" data-testid={`bk-product-qty-${r.product_id}`}>{r.qty}</span>
            <button type="button" onClick={() => setQty(r.product_id, r.qty + 1)} disabled={r.qty >= MAX_QTY} aria-label="Increase quantity" data-testid={`bk-product-inc-${r.product_id}`} className="h-8 w-8 flex items-center justify-center disabled:text-slate-300"><Plus className="h-3.5 w-3.5" /></button>
          </div>
          <span className="w-20 text-right text-sm font-semibold" data-testid={`bk-product-line-${r.product_id}`}>{inr(r.price * r.qty)}</span>
          <button type="button" onClick={() => setRows(rows.filter((x) => x.product_id !== r.product_id))} aria-label="Remove product" data-testid={`bk-product-remove-${r.product_id}`} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {!adding ? (
        <Button type="button" variant="outline" onClick={() => setAdding(true)} data-testid="bk-add-product" className="w-full h-10 gap-1 border-dashed"><Plus className="h-4 w-4" /> Add Product</Button>
      ) : (
        <div className="rounded-xl border border-slate-200 p-2 space-y-2" data-testid="bk-add-product-panel">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" data-testid="bk-add-product-search" className="bg-white pl-9 h-10" />
            </div>
            <button type="button" onClick={() => { setAdding(false); setQ(""); }} aria-label="Close" data-testid="bk-add-product-close" className="text-slate-400 hover:text-slate-600"><X className="h-4 w-4" /></button>
          </div>
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {available.length === 0 && <p className="px-1 py-2 text-xs text-slate-400" data-testid="bk-add-product-empty">No products available.</p>}
            {available.map((p) => (
              <button type="button" key={p.id} onClick={() => add(p)} data-testid={`bk-add-product-${p.id}`}
                className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-emerald-400">
                <span>{p.name} <span className="text-xs text-slate-400">· {p.stock} in stock</span></span>
                <span className="text-slate-600">{inr(p.selling_price)}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
